/**
 * TimelineCropRow Component
 *
 * Individual crop label row with expandable details.
 * Shows crop name, count, and notes when expanded.
 *
 * Also handles "org task rows" — virtual rows for org-level tasks.
 * Org task rows show a label chip with the org name (except in location
 * grouping mode where the group header already identifies the org).
 */

import React, { useState, useRef, useEffect } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { CropScheduleLabelChip } from '../../../crop-schedule-label-chip'
import { useTimelineContextDispatch, setViewStrategy, setExpandedIds, setShowSuccessions } from '../../context/timeline-context'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import {
  EXPANDED_ROW_HEIGHT,
  SUCCESSION_THIN_HEIGHT,
  SUCCESSION_THIN_GAP,
  SUCCESSION_EXPANDED_HEIGHT,
} from '../../timeline-constants'
import { getSuccessionAreaHeight } from '../../../utils/crop-grouping-utils'
import { useEditCropContext } from '~/crop/edit-crop/edit-crop-context'
import { EditCropPopper } from '~/crop/edit-crop/edit-crop-popper'
import { DeleteCropModal } from '~/crop/delete-crop/delete-crop-modal'

/**
 * TimelineCropRow - Individual crop label row
 *
 * @param {Object} props
 * @param {Object} props.crop - Crop object with id, name, count, notes (or isOrgTaskRow)
 * @param {boolean} props.isExpanded - Expansion state
 * @param {boolean} props.isParentOrgHovered - Parent organization hover state
 * @param {Function} props.onToggle - Expansion toggle handler
 */
export const TimelineCropRow = ({ crop, isExpanded, isParentOrgHovered, onToggle }) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { viewStrategy, groupStrategy, showSuccessions, successionMap, expandedIds, readOnly, collapsedRowHeight } = useTimelineStaticContext()
  const prevShowSuccessionsRef = useRef(null)
  const dispatch = useTimelineContextDispatch()
  const [isDeleteCropModalOpen, setIsDeleteCropModalOpen] = useState(false)
  const [chipAnchorEl, setChipAnchorEl] = useState(null)

  const editCtx = useEditCropContext()
  const isEditing = editCtx?.editingCropId === crop.id
  const someOtherCropEditing = editCtx?.editingCropId !== null && !isEditing

  // Capture viewStrategy before editing starts; restore when editing ends
  // Also collapse all other rows when editing starts
  const prevViewStrategyRef = useRef(null)
  const prevExpandedIdsRef = useRef(null)

  useEffect(() => {
    if (isEditing) {
      prevViewStrategyRef.current = viewStrategy
      prevExpandedIdsRef.current = expandedIds
      prevShowSuccessionsRef.current = showSuccessions
      dispatch(setViewStrategy({ viewStrategy: 'both' }))
      // Collapse all other rows; ensure this row is expanded
      dispatch(setExpandedIds({ expandedIds: expandedIds.includes(crop.id) ? [crop.id] : [] }))
      // Always show successions while editing so preview rows are visible
      dispatch(setShowSuccessions({ showSuccessions: true }))
    } else if (prevViewStrategyRef.current !== null) {
      dispatch(setViewStrategy({ viewStrategy: prevViewStrategyRef.current }))
      dispatch(setShowSuccessions({ showSuccessions: prevShowSuccessionsRef.current ?? false }))
      prevViewStrategyRef.current = null
      prevExpandedIdsRef.current = null
      prevShowSuccessionsRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing])

  // Calculate row height: double when showing both phases and tasks
  const baseRowH = isExpanded ? EXPANDED_ROW_HEIGHT : collapsedRowHeight

  // If viewStrategy is 'both' and this is NOT an org task row, double the height.
  const rowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? baseRowH * 2 : baseRowH

  // Extra height for succession sub-rows below this crop
  const successionAreaH = getSuccessionAreaHeight(crop, isExpanded, showSuccessions ? {
    showSuccessions,
    successionMap,
    viewStrategy,
    SUCCESSION_THIN_HEIGHT,
    SUCCESSION_THIN_GAP,
    SUCCESSION_EXPANDED_HEIGHT,
  } : null)

  // Org task rows: render differently based on group strategy
  if (crop.isOrgTaskRow) {
    // In location grouping the group header already labels the org — no chip needed
    if (groupStrategy === 'location') {
      return <Box sx={{ height: rowH }} />
    }

    // Other strategies: show chip with org name; edit/delete console.log (placeholder)
    return (
      <Box sx={{ height: rowH, display: 'flex', alignItems: 'center' }}>
        <CropScheduleLabelChip
          disableActionButtons
          isExpanded={isExpanded}
          isParentOrgHovered={isParentOrgHovered}
          label={crop.name}
          notes={crop.crop_details.notes}
          onClick={onToggle}
          onEdit={() => console.log('[Org Task Row] Edit org task row:', crop.organization_id)}
          onDelete={() => console.log('[Org Task Row] Delete org task row:', crop.organization_id)}
          sx={{
            backgroundColor: isParentOrgHovered
              ? isDark ? theme.palette.info[800] : theme.palette.info[600]
              : isExpanded
                ? isDark ? theme.palette.info[700] : theme.palette.info[500]
                : isDark ? theme.palette.info[600] : theme.palette.info[300],

            color: isParentOrgHovered || isExpanded || isDark
              ? theme.palette.info.contrastText
              : undefined,

            border: isExpanded
              ? `2px solid ${theme.palette.info[500]}`
              : `0.25px solid ${theme.palette.info.dark}`,

            '&:hover': {
              border: isExpanded ? `2px solid ${theme.palette.info.dark}` : `0.25px solid ${theme.palette.info.dark}`,
              color: theme.palette.info.contrastText,
              backgroundColor: isExpanded
                ? isDark ? theme.palette.info[600] : theme.palette.info[700]
                : theme.palette.info[500],
            },
          }}
        />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        height: rowH + successionAreaH,
        ...(isEditing && {
          outline: `2px dashed ${theme.palette.warning.main}`,
          outlineOffset: '-2px',
          borderRadius: 1,
          zIndex: 35,
        }),
      }}
    >
      <Box
        sx={{
          height: rowH,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* Wrap chip in a ref'd box so the Popper can anchor to it */}
        <Box ref={setChipAnchorEl} sx={{ display: 'inline-block' }}>
          <CropScheduleLabelChip
            isExpanded={isExpanded}
            isParentOrgHovered={isParentOrgHovered}
            hasSuccession={!!crop.succession}
            isEditing={isEditing}
            notes={crop.crop_details.notes}
            submitDisabled={!editCtx?.hasEdits}
            disableActionButtons={readOnly || someOtherCropEditing}
            readOnly={readOnly}
            onEdit={() => {
              if (readOnly) return
              if (isEditing) {
                editCtx.handleSubmit()
              } else {
                editCtx?.startEditing(crop.id)
              }
            }}
            onDelete={() => { if (!readOnly) { isEditing ? editCtx.stopEditing() : setIsDeleteCropModalOpen(true) } }}
            label={
              isExpanded ? (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      lineHeight: 1.3,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'normal'
                    }}
                    title={crop.name}
                  >
                    {crop.name}
                  </Typography>
                  <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                    <strong>{crop.count}</strong> plant{crop.count > 1 ? 's' : ''}
                  </Typography>
                  {crop.succession?.count > 0 && (
                    <>
                      <br />
                      <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
                        <strong>{crop.succession.count}</strong> succession{crop.succession.count > 1 ? 's' : ''}
                      </Typography>
                    </>
                  )}
                  {crop.notes && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '180px',
                        lineHeight: 1.2,
                      }}
                      title={crop.notes}
                    >
                      {crop.notes}
                    </Typography>
                  )}
                </Box>
              ) : (
                crop.name
              )
            }
            onClick={isEditing ? undefined : onToggle}
          />
        </Box>

        {/* Inline edit popper — anchored to chip wrapper */}
        {editCtx && (
          <EditCropPopper anchorEl={isEditing ? chipAnchorEl : null} />
        )}

        {isDeleteCropModalOpen && (
          <DeleteCropModal
            cropId={crop.id}
            open={isDeleteCropModalOpen}
            onClose={() => setIsDeleteCropModalOpen(false)}
          />
        )}
      </Box>
    </Box>
  )
}
